import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { FileType } from './types';
import defaultThumbnail from '../../../../assets/recording/DefaultThumbnail.svg';

interface FileListProps {
  files: {
    data: FileType[];
    pagination?: {
      page: number;
      total: number;
    };
  };
  selectedFiles: string[];
  onFileSelect: (fileId: string) => void;
  onScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  libraryStatus: string;
  onFolderChange?: (folderId: string) => void;
  selectedFolderId?: string | null;
  searchTerm?: string;
  selectionModeEnabled?: boolean;
  compact?: boolean;
}

const FileList: React.FC<FileListProps> = ({
  files,
  selectedFiles,
  onFileSelect,
  onScroll,
  libraryStatus,
  searchTerm,
  selectionModeEnabled = false,
  compact = false,
}) => {
  const { t } = useTranslation();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // Removed useTheme dependency
  const [thumbUrlValidity, setThumbUrlValidity] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const newThumbUrlValidity = { ...thumbUrlValidity };
    files.data.forEach((file) => {
      if (!newThumbUrlValidity[file._id]) {
        newThumbUrlValidity[file._id] = file.thumbUrl || defaultThumbnail;
      }
    });
    setThumbUrlValidity(newThumbUrlValidity);
  }, [files.data]);

  // Handle scroll events properly for ScrollArea
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea || !onScroll) return undefined;

    // Find the actual scrollable viewport within the ScrollArea
    const viewport = scrollArea.querySelector(
      '[data-radix-scroll-area-viewport]',
    );

    const handleScrollEvent = (event: Event) => {
      // Create a synthetic React event that matches the expected signature
      const syntheticEvent = {
        target: event.target,
        currentTarget: event.currentTarget,
        preventDefault: event.preventDefault.bind(event),
        stopPropagation: event.stopPropagation.bind(event),
      } as React.UIEvent<HTMLDivElement>;

      onScroll(syntheticEvent);
    };

    // Try multiple approaches to catch scroll events
    const listeners: Array<{
      element: Element | Window;
      handler: (e: Event) => void;
    }> = [];

    // 1. Try the viewport first (most likely to work)
    if (viewport) {
      viewport.addEventListener('scroll', handleScrollEvent, { passive: true });
      listeners.push({ element: viewport, handler: handleScrollEvent });
    }

    // 2. Try the scroll area itself
    scrollArea.addEventListener('scroll', handleScrollEvent, { passive: true });
    listeners.push({ element: scrollArea, handler: handleScrollEvent });

    // 3. Try parent containers
    let parent = scrollArea.parentElement;
    while (parent && parent !== document.body) {
      if (parent.scrollHeight > parent.clientHeight) {
        parent.addEventListener('scroll', handleScrollEvent, { passive: true });
        listeners.push({ element: parent, handler: handleScrollEvent });
        break;
      }
      parent = parent.parentElement;
    }

    // 4. Try window as fallback
    window.addEventListener('scroll', handleScrollEvent, { passive: true });
    listeners.push({ element: window, handler: handleScrollEvent });

    return () => {
      listeners.forEach(({ element, handler }) => {
        element.removeEventListener('scroll', handler);
      });
    };
  }, [onScroll]);

  const handleThumbError = (fileId: string) => {
    setThumbUrlValidity((prev) => ({
      ...prev,
      [fileId]: defaultThumbnail,
    }));
  };

  const noRecordingsMessage = (
    <p className="text-sm text-muted-foreground">
      {t(
        'drawer.multiAskAI.fileList.pleaseSelectFolder',
        'Please select a folder from the dropdown above to view recordings',
      )}
    </p>
  );

  const formatDate = (date: string | Date) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  // Helper function to clean HTML tags and normalize text
  const cleanText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/p><p>/g, ' ') // Replace paragraph breaks with spaces
      .replace(/p>/g, '') // Remove remaining paragraph tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
  };

  // Memoized file filtering function
  const filteredFiles = useMemo(() => {
    if (!searchTerm || !files.data) return files.data;

    try {
      const normalizedSearchTerm = searchTerm.toLowerCase().trim();
      if (!normalizedSearchTerm) return files.data;

      return files.data.filter((file) => {
        if (!file?.name) return false;
        const cleanName = cleanText(file.name);
        return cleanName.toLowerCase().includes(normalizedSearchTerm);
      });
    } catch (error) {
      console.error('Error filtering files:', error);
      return files.data;
    }
  }, [files.data, searchTerm]);

  // Highlight search terms in text - simplified and more stable
  const highlightText = (text: string): React.ReactNode => {
    if (!searchTerm || !text || typeof text !== 'string') return text;

    try {
      // Clean and normalize the text
      const cleanedText = cleanText(text);

      const escapedTerm = searchTerm
        .trim()
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (!escapedTerm) return cleanedText;

      const regex = new RegExp(`(${escapedTerm})`, 'gi');
      const parts = cleanedText.split(regex);

      return parts.map((part, index) =>
        regex.test(part) ? (
          <mark
            key={index}
            style={{
              backgroundColor: '#fff176',
              color: '#000',
              borderRadius: '2px',
              padding: '0 2px',
            }}
          >
            {part}
          </mark>
        ) : (
          part
        ),
      );
    } catch (error) {
      console.error('Search highlighting error:', error);
      return text;
    }
  };

  const renderFileListContent = () => {
    if (libraryStatus === 'pending') {
      return (
        <div className="space-y-3 p-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="border-0 shadow-none">
              <CardContent className="p-3">
                <div className="flex items-center space-x-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (libraryStatus === 'failed') {
      return (
        <div className="flex items-center justify-center py-16 px-8">
          <Card className="border-destructive/50 bg-destructive/10">
            <CardContent className="p-4">
              <p className="text-sm text-destructive">
                {t(
                  'drawer.multiAskAI.fileList.failedToLoadLibrary',
                  'Failed to load library. Please try again later.',
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (!filteredFiles || filteredFiles.length === 0) {
      return (
        <div className="flex items-center justify-center py-16 px-8">
          <div className="text-center">
            {searchTerm ? (
              <p className="text-sm text-muted-foreground">
                {t(
                  'drawer.multiAskAI.fileList.noRecordingsFound',
                  'No recordings found matching',
                )}{' '}
                &ldquo;{searchTerm}&rdquo;
              </p>
            ) : (
              noRecordingsMessage
            )}
          </div>
        </div>
      );
    }

    return filteredFiles.map((file) => {
      const thumbUrlValid =
        thumbUrlValidity[file._id] || file.thumbUrl || defaultThumbnail;
      const hasRealThumbnail = !!file.thumbUrl;
      const cleanFileName = cleanText(file.name);
      const isDisabled = !file.textData?.transcriptUrl;

      return (
        <Card
          key={file._id}
          className={cn(
            'border-0 rounded-none border-b last:border-b-0 shadow-none transition-colors',
            isDisabled && 'opacity-60',
            selectedFiles.includes(file._id) && 'bg-accent',
          )}
        >
          <CardContent
            className={cn(
              'flex items-center gap-3 cursor-pointer transition-colors',
              compact ? 'p-3' : 'p-4',
              !isDisabled && 'hover:bg-accent/50',
              isDisabled && 'cursor-not-allowed',
            )}
            onClick={() => !isDisabled && onFileSelect(file._id)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                e.preventDefault();
                onFileSelect(file._id);
              }
            }}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
          >
            {selectionModeEnabled && (
              <Checkbox
                checked={selectedFiles.includes(file._id)}
                disabled={isDisabled}
                onCheckedChange={() => !isDisabled && onFileSelect(file._id)}
                onClick={(e) => e.stopPropagation()}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
            )}
            {hasRealThumbnail ? (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'relative p-0 group',
                  compact ? 'h-8 w-8' : 'h-10 w-10',
                )}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const baseUrl = window.location.pathname.includes('/app')
                    ? '/app/#'
                    : '/#';
                  window.open(
                    `${baseUrl}/library/${file.teamId}/recents/${file._id}`,
                    '_blank',
                  );
                }}
              >
                <div className="relative w-full h-full rounded-md overflow-hidden">
                  <img
                    src={thumbUrlValid}
                    alt="File thumbnail"
                    onError={() => handleThumbError(file._id)}
                    className="w-full h-full object-cover"
                    style={{
                      objectFit:
                        thumbUrlValid === defaultThumbnail
                          ? 'cover'
                          : 'contain',
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="h-4 w-4 text-white" />
                  </div>
                </div>
              </Button>
            ) : (
              <div
                className={cn(
                  'flex-shrink-0 flex items-center justify-center rounded-md overflow-hidden bg-muted',
                  compact ? 'w-8 h-8' : 'w-10 h-10',
                )}
              >
                <img
                  src={defaultThumbnail}
                  alt={t(
                    'drawer.multiAskAI.fileList.defaultThumbnail',
                    'Default thumbnail',
                  )}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div
                className={cn(
                  'line-clamp-2 leading-tight',
                  compact ? 'text-xs mb-1' : 'text-sm mb-2',
                  selectedFiles.includes(file._id)
                    ? 'font-semibold'
                    : 'font-normal',
                )}
              >
                {highlightText(cleanFileName)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatDate(file.createdAt)}
              </div>
            </div>
          </CardContent>
        </Card>
      );
    });
  };

  return (
    <ScrollArea ref={scrollAreaRef} className="h-full">
      <div className="space-y-0">{renderFileListContent()}</div>
    </ScrollArea>
  );
};

export default FileList;
